import { CustomSearchType } from '@/components/growth/search-section-city/types';
import WPTypography from '@/components/typography';
import { useMedia } from '@drivekyte/ui';
import useTranslation from '@/hooks/use-translation';
import updateHeroTitleFromUrlParams from '@/utils/update-hero-title-from-url-params';
import { Box, Hidden } from '@drivekyte/web-components';
import { useEffect } from 'react';
import styled from 'styled-components';
import SearchBox from './components/search-box';
import WhereAndWhen from './components/where-and-when/where-and-when';

type SearchProps = {
  title?: string;
  city?: string;
  subTitle?: string;
  custom?: CustomSearchType;
  lightText?: boolean;
};

type ShadowTextProps = {
  enabled?: boolean;
};

const Search = ({ title, city, subTitle, custom, lightText }: SearchProps) => {
  const { t } = useTranslation('components.growth.search');
  const media = useMedia();
  const isMobile = media.xs;
  const isTablet = media.sm;

  const largeHeadlineStyle = {
    fontSize: isMobile ? '24px' : '48px',
    lineHeight: isMobile ? '36px' : '64px',
  };

  useEffect(() => {
    updateHeroTitleFromUrlParams();
  }, []);

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent={isMobile ? 'center' : 'flex-start'}
        marginBottom="medium"
      >
        <WPTypography
          variant="h1"
          color={lightText ? 'secondary' : 'primary'}
          fontFamily="Gilroy"
          fontWeight={700}
          fontSize={isMobile ? '20' : 28}
          lineHeight={36}
          textAlign={isMobile ? 'center' : 'left'}
          dataConceptualExperiment="title"
        >
          {custom === 'colorado' && t('road-trip')}
          {custom === 'road-trips-in-state' && title}
          {custom === 'road-trips' && (
            <span style={largeHeadlineStyle}>
              {title} <TertiaryText>{city}</TertiaryText>
            </span>
          )}
          {custom === 'airports' && (
            <TertiaryText style={largeHeadlineStyle}>{city}</TertiaryText>
          )}
          {custom === 'self-pickup' && (
            <>
              {title || t('self-pickup.title')}
              <TertiaryText>{` ${city || t('title-city')}`}</TertiaryText>
            </>
          )}
          {!custom && (
            <>
              {title || t('title')}
              <TertiaryText>{` ${city || t('title-city')}`}</TertiaryText>
            </>
          )}{' '}
          <br />
          <span style={largeHeadlineStyle}>
            <ShadowText enabled={!isTablet && lightText}>
              {custom === 'colorado' && (
                <>
                  {t('denver-to')}
                  <TertiaryText>{` ${city}`}</TertiaryText>
                </>
              )}
              {custom === 'road-trips-in-state' && (
                <>
                  {`${subTitle?.split(' to ')[0]} to `}
                  <TertiaryText>{subTitle?.split(' to ')[1]}</TertiaryText>
                </>
              )}
              {custom === 'airports' && <>{title || t('airports.title')}</>}
              {custom === 'self-pickup' && (
                <>{title || t('self-pickup.sub-title')}</>
              )}
              {!custom && (subTitle || t('sub-title'))}
            </ShadowText>
          </span>
        </WPTypography>
      </Box>
      <Hidden smDown>
        <SearchBox />
      </Hidden>
      <Hidden only={['xl', 'lg', 'md']}>
        <WhereAndWhen />
      </Hidden>
    </Box>
  );
};

const TertiaryText = styled.span`
  color: ${({ theme }) => theme.colors.tertiary};
`;
const ShadowText = styled.span<ShadowTextProps>`
  ${({ theme, enabled }) => (enabled ? theme.colors.primary : 'transparent')};
  white-space: pre-line;
`;

export default Search;
